import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { Loading } from "~/components/reusable/Loading"
import { cn } from "~/utils/helpers"

import { usePermissionGate, type PermissionGateOptions } from "./PermissionTooltip"

export type ButtonSize = "base" | "sm" | "xs" | "2xs"

const buttonVariants = cva(
  "rounded-full [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 items-center justify-center whitespace-nowrap transition focus-visible:outline-hidden focus-visible:outline-hidden disabled:cursor-not-allowed disabled:shadow-button-neutral disabled:bg-button-disabled disabled:bg-none disabled:text-fg-disabled disabled:[&_svg]:fill-fg-disabled cursor-pointer",
  {
    variants: {
      variant: {
        primary:
          "bg-button-primary [background-image:linear-gradient(180deg,rgba(255,255,255,0.1),rgba(255,255,255,0))] shadow-button-primary text-fg-on-color [&_svg]:fill-fg-on-color hover:bg-button-primary-hover focus-visible:shadow-button-primary-focus active:shadow-button-primary active:bg-button-primary-pressed",
        neutral:
          "bg-button-neutral shadow-button-neutral text-fg-base [&_svg]:fill-fg-subtle hover:bg-button-neutral-hover focus-visible:shadow-button-neutral-focus active:shadow-button-neutral active:bg-button-neutral-pressed",
        transparent:
          "bg-button-transparent text-fg-base [&_svg]:fill-fg-subtle hover:bg-button-transparent-hover focus-visible:bg-button-transparent-focus focus-visible:shadow-button-transparent-focus active:bg-button-transparent-pressed",
        danger:
          "bg-button-danger [background-image:linear-gradient(180deg,rgba(255,255,255,0.1),rgba(255,255,255,0))] text-fg-on-color shadow-button-danger [&_svg]:fill-fg-on-color hover:bg-button-danger-hover focus-visible:shadow-button-danger-focus active:shadow-button-danger",
      },
      size: {
        "2xs": "h-5 px-1 py-0 label-base",
        xs: "h-6 px-1.5 py-0.5 label-base",
        sm: "h-7 px-2 py-1 label-base",
        base: "h-8 px-2.5 py-1.5 label-base",
      },
    },
    compoundVariants: [
      {
        // The base's disabled treatment is a neutral button's — a ring over an opaque fill —
        // which would make these MORE prominent disabled than enabled. They stay flat and read
        // as disabled by the muted foreground the base already applies.
        variant: "transparent",
        class: "disabled:shadow-none disabled:bg-transparent",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "base",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants>,
    PermissionGateOptions {
  asChild?: boolean
  loading?: boolean
  /**
   * When set, Button renders as `<a>` instead of `<button>` — use this for external
   * links and route navigation. Pass `target="_blank"` for new-tab links; `rel`
   * defaults to `"noopener noreferrer"` in that case.
   */
  href?: string
  target?: React.HTMLAttributeAnchorTarget
  rel?: string
}

/**
 * Every action in the app. Variants by hierarchy: primary / neutral / transparent / danger.
 * Pass `href` to render as a link. Pass `requires` to permission-gate the action —
 * disables itself and renders its own permission tooltip.
 * Canonical intent: {@link ../design-system/intents.ts BUTTON_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      href,
      target,
      rel,
      disabled,
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      tooltipClassName,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const { blocked, gate } = usePermissionGate({
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      tooltipClassName,
      wrapperClassName,
    })
    const isLink = href !== undefined
    const Comp = (asChild ? Slot : isLink ? "a" : "button") as React.ElementType

    const linkProps = isLink
      ? {
          href,
          target,
          rel: rel ?? (target === "_blank" ? "noopener noreferrer" : undefined),
        }
      : {}

    const button = (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref as React.Ref<HTMLElement> as never}
        disabled={isLink ? undefined : disabled || loading || blocked}
        {...(!isLink && !asChild && { type: "button" })}
        {...linkProps}
        {...props}
      >
        {/* This div needs to be there when asChild is used, as there must only be one JSX element */}
        <div
          className={cn("gap-1.5 inline-flex justify-center items-center", {
            "cursor-not-allowed opacity-50": loading,
          })}
        >
          {loading && <Loading size="sm" />}
          {props.children}
        </div>
      </Comp>
    )

    return gate(button)
  },
)

Button.displayName = "Button"

export type IconButtonProps = ButtonProps

/**
 * Square button wrapping a single Phosphor icon. Same variants/sizes as Button.
 * Always pass `aria-label`.
 * Canonical intent: {@link ../design-system/intents.ts ICON_BUTTON_INTENT}.
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, size = "base", loading, ...props }, ref) => {
    let paddingClass = ""
    let squareSizeClass = ""
    switch (size) {
      case "base":
        paddingClass = "p-2"
        squareSizeClass = "w-8 h-8"
        break
      case "sm":
        paddingClass = "p-1.5"
        squareSizeClass = "w-7 h-7"
        break
      case "xs":
        paddingClass = "p-1"
        squareSizeClass = "w-6 h-6"
        break
      case "2xs":
        paddingClass = "p-0.5"
        squareSizeClass = "w-5 h-5"
        break
    }

    return (
      <Button
        ref={ref}
        size={size}
        loading={loading}
        className={cn(paddingClass, squareSizeClass, className)} // Apply square size and padding
        {...props}
      >
        {!loading && props.children}
      </Button>
    )
  },
)

IconButton.displayName = "IconButton"

export { Button, buttonVariants, IconButton }
